import React, { useState } from 'react';
import styled from '@emotion/styled';
import { colors, layout, mediaQuery } from '@modernhealth/vital-foundation';
import { HeadingText60, HeadingText20 } from '@modernhealth/vital-components';
import { useTranslation } from 'react-i18next';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import moment from 'moment';

import { useLocalizedVersion } from 'src/shared/hooks/useLocalizedVersion';

import { BackButton } from '../components/BackButton';
import DisplayUpcomingBySeries from '../components/DisplayUpcomingBySeries';
import DisplayUpcomingByTime from '../components/DisplayUpcomingByTime';

const Wrapper = styled.div({
    width: '100%',
    maxWidth: layout.containerWidth,
    margin: '0 auto',
    paddingLeft: layout.containerPaddingX,
    paddingRight: layout.containerPaddingX,
    paddingBottom: layout.footerHeight,
});

export const UpcomingCirclesRowSubheader = styled.div({
    display: 'flex',
    flex: 'row nowrap',
    justifyContent: 'space-between',
});

export const UpcomingCirclesRow = styled.div({
    display: 'grid',
    gridTemplateColumns: '1fr 0 0',
    gridTemplateRows: '1fr 0',
    gap: 0,
    overflow: 'hidden',
    [mediaQuery.minWidth('md')]: {
        gap: '0 1rem',
        gridTemplateColumns: 'repeat(2, 1fr)',
    },
    [mediaQuery.minWidth('lg')]: {
        gridTemplateColumns: 'repeat(3, 1fr)',
    },
});

const DisplayTypes = styled.div({
    display: 'flex',
    gap: 9,
    marginBottom: 48,
});

interface DisplayTabProps {
    active: boolean;
}

const DisplayTab = styled.span<DisplayTabProps>`
    width: 85px;
    height: 32px;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border-radius: 6px;
    background-color: ${props => {
        return props.active ? colors.gray85 : 'transparent';
    }};
    &:hover {
        background-color: ${props => {
            return props.active ? colors.gray85 : colors.gray05;
        }};
    }
`;

const UpcomingCirclesPage: React.FC = () => {
    const { t } = useTranslation('circles');
    const history = useHistory();
    const [localizedVersion] = useLocalizedVersion();
    const { path } = useRouteMatch();
    // TODO: Remove loacalizedVersion logic from this file once translations are ready for the new strings
    const isDisplayByTime = !!useRouteMatch({ path: `${path}/`, exact: true });

    const localizedPath = !localizedVersion ? `${path}/series/` : '*';
    const [currentMoment] = useState(moment().toISOString());

    return (
        <Wrapper css={{ marginTop: 64 }}>
            <BackButton fallbackPath="/circles">
                {previousRoute => {
                    if (previousRoute === '/circles') {
                        return 'Circles';
                    }

                    return null;
                }}
            </BackButton>
            <HeadingText60 as="h2" fontColor="gray85" css={{ marginBottom: 32 }}>
                {t('pages.upcoming.header')}
            </HeadingText60>
            {!localizedVersion && (
                <DisplayTypes>
                    <DisplayTab
                        onClick={() => {
                            history.replace('/circles/upcoming');
                        }}
                        active={isDisplayByTime}
                    >
                        <HeadingText20 fontColor={isDisplayByTime ? 'white' : 'gray85'}>
                            {t('pages.upcoming.by_time.button')}
                        </HeadingText20>
                    </DisplayTab>
                    <DisplayTab
                        onClick={() => {
                            history.replace('/circles/upcoming/series');
                        }}
                        active={!isDisplayByTime}
                    >
                        <HeadingText20 fontColor={!isDisplayByTime ? 'white' : 'gray85'}>
                            {t('pages.upcoming.by_series.button')}
                        </HeadingText20>
                    </DisplayTab>
                </DisplayTypes>
            )}
            {/* TODO: Re-query for sessions in the background after a certain amount of time */}
            <Switch>
                {!localizedVersion ? (
                    <Route
                        path={`${path}`}
                        exact
                        render={() => <DisplayUpcomingByTime currentMoment={currentMoment} />}
                    />
                ) : null}
                <Route path={localizedPath} component={DisplayUpcomingBySeries} exact />
            </Switch>
        </Wrapper>
    );
};

export default UpcomingCirclesPage;
